import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, InputNumber, DatePicker, Select, Checkbox, Button, Tabs, Tag,
  Statistic, Row, Col, Space, Divider, Tooltip,
} from 'antd';
import { BellOutlined, SaveOutlined, ArrowRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import bilateralesService from '../../../services/bilateralesService';
import { STATUTS_SINISTRE, formatDate } from '../../../variables/constants';
import InsuffisancesPanel from './InsuffisancesPanel';
import { filtreSansAccents } from '../../../utils/rechercheTexte';
import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';

/**
 * Décision de séance sur un dossier bilatéral.
 *
 * <h3>Pourquoi une modale et non un tiroir</h3>
 * Cet écran se remplit EN SÉANCE, pendant que la compagnie et le bureau homologue
 * discutent le dossier à voix haute. On y arbitre un montant et on y change le statut
 * du sinistre - deux actes engageants.
 *
 * Un tiroir latéral laisse le tableau de la session visible derrière lui : le regard
 * continue de circuler entre la ligne du fond et le formulaire, et sur un écran de
 * réunion on ne sait plus quel dossier on est en train de trancher. La modale centre
 * l'attention sur un dossier et un seul, avec son numéro et sa compagnie en titre.
 *
 * `maskClosable` est volontairement désactivé : un clic à côté ne doit pas fermer un
 * formulaire de négociation à moitié saisi.
 */

const TAUX_FG = 0.05;
const fmt = (v) => v != null ? new Intl.NumberFormat(localeIntl()).format(v) : '-';

export default function DossierBilateralModal({ open, dossier, onClose, onSaved }) {
  const { t } = useTranslation('bilaterales');
  const [form] = Form.useForm();
  const [calc, setCalc] = useState({ fgTg: null, bapTotal: null });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !dossier) return;
    form.setFieldsValue({
      montantReclame: dossier.montantReclame,
      montantBap: dossier.montantBap,
      nouveauStatutSinistre: dossier.nouveauStatutSinistre || dossier.statutSinistreActuel,
      decisionSeance: dossier.decisionSeance,
      blocageIdentifie: dossier.blocageIdentifie,
      observations: dossier.observations,
      expertiseDemandee: dossier.expertiseDemandee,
      datePromesseAssureur: dossier.datePromesseAssureur ? dayjs(dossier.datePromesseAssureur) : null,
    });
    setCalc({ fgTg: dossier.fgTg, bapTotal: dossier.bapTotal });
  }, [open, dossier, form]);

  const onBapChange = (val) => {
    if (val == null) { setCalc({ fgTg: null, bapTotal: null }); return; }
    const fg = Math.round(val * TAUX_FG);
    setCalc({ fgTg: fg, bapTotal: val + fg });
  };

  const enregistrer = async () => {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        montantReclame: vals.montantReclame,
        montantBap: vals.montantBap,
        nouveauStatutSinistre: vals.nouveauStatutSinistre,
        decisionSeance: vals.decisionSeance,
        blocageIdentifie: vals.blocageIdentifie,
        observations: vals.observations,
        expertiseDemandee: vals.expertiseDemandee,
        datePromesseAssureur: vals.datePromesseAssureur
          ? vals.datePromesseAssureur.format('YYYY-MM-DD') : null,
      };
      await bilateralesService.enregistrerDecision(dossier.dbTrackingId, payload);
      toast.success(t('dossier.decisionEnregistree'));
      onSaved?.();
    } finally { setSaving(false); }
  };

  const envoyerRelance = async () => {
    setSending(true);
    try {
      await bilateralesService.relanceManuelle(dossier.dbTrackingId);
      toast.success(t('dossier.relanceEnvoyee'));
    } catch (_) {
    } finally { setSending(false); }
  };

  if (!dossier) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      destroyOnClose
      maskClosable={false}
      styles={{ body: { paddingTop: 8 } }}
      title={(
        <Space wrap>
          <span style={{ fontWeight: 700 }}>{dossier.numeroSinistreLocal}</span>
          <Tag>{dossier.compagnieRaisonSociale}</Tag>
          {dossier.rbPrecedenteReference && (
            <Tag color="gold">{t('dossier.dejaDiscute', { reference: dossier.rbPrecedenteReference })}</Tag>
          )}
        </Space>
      )}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {/* Ouverture dans un onglet séparé : en séance, on consulte le dossier sans
              perdre la saisie de négociation en cours. */}
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => window.open(`/sinistres/${dossier.sinistreTrackingId}`, '_blank', 'noopener')}
          >
            {t('dossier.ouvrirSinistre')}
          </Button>
          {dossier.datePromesseAssureur && (
            <Button icon={<BellOutlined />} onClick={envoyerRelance} loading={sending}>
              {t('dossier.relanceManuelle')}
            </Button>
          )}
          <Button onClick={onClose}>{t('actions.fermer')}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={enregistrer}
            loading={saving}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
          >
            {t('dossier.enregistrerDecision')}
          </Button>
        </div>
      )}
    >
      <Tabs
        defaultActiveKey="decision"
        items={[
          {
            key: 'decision',
            label: t('dossier.onglets.decision'),
            children: (
              <Form form={form} layout="vertical">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label={t('dossier.montantReclame')} name="montantReclame">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(v) => new Intl.NumberFormat(localeIntl()).format(v || 0)}
                        parser={(v) => v.replace(/[^\d]/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('dossier.montantBap')} name="montantBap">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(v) => new Intl.NumberFormat(localeIntl()).format(v || 0)}
                        parser={(v) => v.replace(/[^\d]/g, '')}
                        onChange={onBapChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12} style={{ background: '#f9fafb', padding: 8, borderRadius: 6, marginBottom: 12 }}>
                  <Col span={12}>
                    <Statistic title={t('session.colonnes.fgTg')} value={fmt(calc.fgTg)} suffix="FCFA"
                      valueStyle={{ fontSize: 16, color: '#6b7280' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title={t('session.colonnes.bapTotal')} value={fmt(calc.bapTotal)} suffix="FCFA"
                      valueStyle={{ fontSize: 16, fontWeight: 600 }} />
                  </Col>
                </Row>

                <Form.Item
                  label={(
                    <span>
                      {t('dossier.nouveauStatut')}
                      <Tooltip title={t('dossier.nouveauStatutAide')}>
                        <span style={{ marginLeft: 6, color: '#c47800', fontWeight: 700 }}>?</span>
                      </Tooltip>
                    </span>
                  )}
                  name="nouveauStatutSinistre"
                >
                  <Select
                    showSearch
                    filterOption={filtreSansAccents}
                    optionFilterProp="label"
                    options={STATUTS_SINISTRE.map(s => ({ value: s.value, label: s.label }))}
                    placeholder={t('dossier.garderStatut')}
                    allowClear
                  />
                </Form.Item>

                <Form.Item label={t('dossier.decisionNotes')} name="decisionSeance">
                  <Input.TextArea rows={4} placeholder={t('dossier.decisionNotesExemple')} />
                </Form.Item>

                <Form.Item
                  label={(
                    <span>
                      {t('dossier.datePromise')}
                      <Tooltip title={t('dossier.datePromiseAide')}>
                        <BellOutlined style={{ marginLeft: 6, color: '#c47800' }} />
                      </Tooltip>
                    </span>
                  )}
                  name="datePromesseAssureur"
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>

                <Form.Item name="expertiseDemandee" valuePropName="checked">
                  <Checkbox>{t('dossier.expertiseDemandee')}</Checkbox>
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label={t('dossier.blocage')} name="blocageIdentifie">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('dossier.observations')} name="observations">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            ),
          },
          {
            key: 'insuffisances',
            label: t('dossier.onglets.insuffisances'),
            children: <InsuffisancesPanel sinistreTrackingId={dossier.sinistreTrackingId} />,
          },
          {
            key: 'historique',
            label: t('dossier.onglets.historique'),
            children: (
              <div style={{ color: '#6b7280' }}>
                {t('dossier.historique.pourTimeline')}{' '}
                <a href={`/sinistres/${dossier.sinistreTrackingId}`} target="_blank" rel="noreferrer">
                  {t('dossier.historique.ouvrirNouvelOnglet')}
                </a>
                <Divider />
                {dossier.datePromesseAssureur && (
                  <div>
                    <Trans
                      t={t}
                      i18nKey="dossier.historique.engagement"
                      values={{ datePromesse: formatDate(dossier.datePromesseAssureur), dateRappel: formatDate(dossier.dateRappel) }}
                      components={{ 1: <strong /> }}
                    />
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}
